import { ReactNode, useCallback, useEffect, useState } from 'react';

import { Box, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  CheckCircleOutline,
  DnsOutlined,
  ErrorOutline,
  RefreshOutlined,
  SensorsOutlined
} from '@mui/icons-material';

import { useCookies } from 'react-cookie';

import TotalItemCard from './TotalItemCard';
import TotalDonutChart from './TotalDonutChart';
import TotalRankingBarChart, { RankingBarChartItem } from './TotalRankingBarChart';

import {
  DashboardTrackingGeofenceCountType,
  DashboardTrackingStatusCountType,
  SelectedParametersType
} from '../../../../types/types';
import findTrackingData from '../../../../resources/Api/dashboard/findTrackingData';
import findCustomerTrackingData from '../../../../resources/Api/dashboard/findCustomerTrackingData';
import findBackendHealth from '../../../../resources/Api/dashboard/findBackendHealth';
// import findTraccarHealth from '../../../../resources/Api/dashboard/findTraccarHealth';
import { vehicleIgnitionStatusColors } from '../../../../functions/functions';
import { DASHBOARD_DISTANCE_FUEL_ENABLED } from '../../../../consts/runtimeConfig';

const movementStatusColors = {
  'Em movimento': vehicleIgnitionStatusColors.moving,
  'Parado com ignição': vehicleIgnitionStatusColors.idle,
  'Parado': vehicleIgnitionStatusColors.off,
};

type ServiceHealthStatus = 'CHECKING' | 'UP' | 'DOWN' | 'NOT_CONFIGURED';

type TrackingTabProps = {
  scope?: 'manager' | 'customer';
};

type ServiceHealthIndicatorProps = {
  description: string;
  icon: ReactNode;
  name: string;
  status: ServiceHealthStatus;
};

const healthStatusPresentation: Record<ServiceHealthStatus, { label: string; className: string }> = {
  CHECKING: {
    label: 'Verificando',
    className: 'tw-bg-slate-200 tw-text-slate-600 dark:tw-bg-slate-700 dark:tw-text-slate-200',
  },
  UP: {
    label: 'Disponível (UP)',
    className: 'tw-bg-green-100 tw-text-green-700 dark:tw-bg-green-950/50 dark:tw-text-green-300',
  },
  DOWN: {
    label: 'Indisponível (DOWN)',
    className: 'tw-bg-red-100 tw-text-red-700 dark:tw-bg-red-950/50 dark:tw-text-red-300',
  },
  NOT_CONFIGURED: {
    label: 'Aguardando integração',
    className: 'tw-bg-slate-200 tw-text-slate-600 dark:tw-bg-slate-700 dark:tw-text-slate-200',
  },
};

const ServiceHealthIndicator = ({ description, icon, name, status }: ServiceHealthIndicatorProps) => {
  const presentation = healthStatusPresentation[status];

  return (
    <Box className="tw-grid tw-min-w-0 tw-grid-cols-[auto_minmax(0,1fr)] tw-items-center tw-gap-x-3 tw-gap-y-2 tw-rounded-xl tw-bg-gtr-surface-muted tw-p-3 sm:tw-grid-cols-[auto_minmax(0,1fr)_auto] sm:tw-p-4">
      <Box className="tw-row-span-2 tw-flex tw-h-11 tw-w-11 tw-flex-none tw-self-start tw-items-center tw-justify-center tw-rounded-xl tw-bg-gtr-teal/10 tw-text-gtr-teal dark:tw-bg-cyan-300/10 dark:tw-text-cyan-300 sm:tw-row-span-1 sm:tw-self-auto">
        {icon}
      </Box>
      <Box className="tw-min-w-0 tw-flex-1">
        <Typography className="!tw-font-prometo !tw-text-sm !tw-font-bold !tw-text-gtr-text">
          {name}
        </Typography>
        <Typography className="!tw-mt-0.5 !tw-font-prometo !tw-text-xs !tw-text-gtr-text-muted">
          {description}
        </Typography>
      </Box>
      <Box
        aria-live="polite"
        className={`tw-col-start-2 tw-flex tw-w-fit tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2.5 tw-py-1 tw-font-prometo tw-text-[11px] tw-font-bold sm:tw-col-start-3 ${presentation.className}`}
      >
        {status === 'CHECKING' && <CircularProgress size={13} thickness={5} color="inherit" />}
        {status === 'UP' && <CheckCircleOutline sx={{ fontSize: 15 }} />}
        {status === 'DOWN' && <ErrorOutline sx={{ fontSize: 15 }} />}
        <span>{presentation.label}</span>
      </Box>
    </Box>
  );
};

const TrackingTab = ({ scope = 'manager' }: TrackingTabProps) => {
  const [cookies] = useCookies(["selectedParameters", "token"]);
  const isCustomerScope = scope === 'customer';

  const [isLoading, setLoading] = useState(false);

  const [deviceHealth, setDeviceHealth] = useState<DashboardTrackingStatusCountType[]>([]);
  const [deviceConnectivity, setDeviceConnectivity] = useState<DashboardTrackingStatusCountType[]>([]);
  const [deviceMovementStatus, setDeviceMovementStatus] = useState<DashboardTrackingStatusCountType[]>([]);
  const [deviceInventory, setDeviceInventory] = useState<DashboardTrackingStatusCountType[]>([]);
  const [deviceActivationStatus, setDeviceActivationStatus] = useState<DashboardTrackingStatusCountType[]>([]);
  const [geofencesByCustomer, setGeofencesByCustomer] = useState<DashboardTrackingGeofenceCountType[]>([]);
  const [totalKmByVehicle, setTotalKmByVehicle] = useState<RankingBarChartItem[]>([]);
  const [fuelConsumptionByVehicle, setFuelConsumptionByVehicle] = useState<RankingBarChartItem[]>([]);
  const [backendHealth, setBackendHealth] = useState<ServiceHealthStatus>('CHECKING');
  const [traccarHealth, setTraccarHealth] = useState<ServiceHealthStatus>('UP');

  const checkBackendHealth = useCallback(async () => {
    const token: string | undefined = cookies.token || sessionStorage.getItem("token") || undefined;

    setBackendHealth('CHECKING');
    try {
      const { data } = await findBackendHealth(token);
      setBackendHealth(data?.status?.toUpperCase() === 'UP' ? 'UP' : 'DOWN');
    } catch {
      setBackendHealth('DOWN');
    }
  }, [cookies.token]);

  // Pausa temporária: loading visual seguido de UP, sem consultar a saúde real do túnel.
  const checkTraccarHealth = useCallback(() => {
    setTraccarHealth('CHECKING');
  }, []);

  useEffect(() => {
    if (traccarHealth !== 'CHECKING') return;

    const loadingTimeout = setTimeout(() => setTraccarHealth('UP'), 500);
    return () => clearTimeout(loadingTimeout);
  }, [traccarHealth]);

  // Reativar a consulta abaixo e seu import após revisar o health check da telemetria.
  /*
  const checkTraccarHealth = useCallback(async () => {
    const token: string | undefined = cookies.token || sessionStorage.getItem("token") || undefined;

    setTraccarHealth('CHECKING');
    try {
      const { data } = await findTraccarHealth(token);
      setTraccarHealth(data?.status?.toUpperCase() === 'UP' ? 'UP' : 'DOWN');
    } catch {
      setTraccarHealth('DOWN');
    }
  }, [cookies.token]);
  */

  const getTrackingData = useCallback(
    async () => {
      const token: string = cookies.token || sessionStorage.getItem("token");

      setLoading(true);
      try {
        const currentParameters: SelectedParametersType = cookies.selectedParameters;
        const managerId = currentParameters?.customer?.id;
        if (!isCustomerScope && !managerId) return;

        const { data } = isCustomerScope
          ? await findCustomerTrackingData(token)
          : await findTrackingData(token, managerId!);

        setDeviceHealth(data.deviceHealth || []);
        setDeviceConnectivity(data.deviceConnectivity || []);
        setDeviceMovementStatus(data.deviceMovementStatus || []);
        setDeviceInventory(data.deviceInventory || []);
        setDeviceActivationStatus(data.deviceActivationStatus || []);
        setGeofencesByCustomer(data.geofencesByCustomer || []);

        if (DASHBOARD_DISTANCE_FUEL_ENABLED) {
          setTotalKmByVehicle(
            (data.totalKmByVehicle || []).map((it) => ({
              label: `${it.vehicleName} (${it.vehiclePlate})`,
              value: Math.round(it.km * 10) / 10
            }))
          );

          setFuelConsumptionByVehicle(
            (data.fuelConsumptionByVehicle || []).map((it) => ({
              label: `${it.vehicleName} (${it.vehiclePlate})`,
              value: Math.round(it.fuelUsed * 10) / 10
            }))
          );
        }
      } catch (error: any) {
        console.error("Dashboard tracking request error:", {
          method: error?.config?.method,
          url: error?.config?.url,
          status: error?.response?.status,
          response: error?.response?.data,
          message: error?.message,
        });
        setDeviceHealth([]);
        setDeviceConnectivity([]);
        setDeviceMovementStatus([]);
        setDeviceInventory([]);
        setDeviceActivationStatus([]);
        setGeofencesByCustomer([]);
        setTotalKmByVehicle([]);
        setFuelConsumptionByVehicle([]);
      } finally {
        setLoading(false);
      }
    },
    [cookies.token, cookies.selectedParameters, isCustomerScope]
  );

  useEffect(() => {
    getTrackingData();
  }, [getTrackingData]);

  useEffect(() => {
    if (isCustomerScope) return;
    void checkBackendHealth();
    void checkTraccarHealth();
  }, [checkBackendHealth, checkTraccarHealth, isCustomerScope]);

  return (
    <Box className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-3 tw-px-3 tw-pb-4">
      {!isCustomerScope && <Paper
        elevation={0}
        className="tw-rounded-xl tw-bg-gtr-surface tw-p-3 tw-shadow-none sm:tw-p-4"
      >
        <Box className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
          <Box className="tw-min-w-0">
            <Typography className="!tw-font-prometo !tw-text-base !tw-font-bold !tw-text-gtr-text">
              Saúde dos serviços
            </Typography>
            <Typography className="!tw-mt-0.5 !tw-font-prometo !tw-text-xs !tw-text-gtr-text-muted">
              Disponibilidade dos serviços essenciais da operação.
            </Typography>
          </Box>
          <Tooltip title="Verificar saúde dos serviços" arrow>
            <span>
              <IconButton
                aria-label="Verificar saúde dos serviços"
                onClick={() => {
                  void checkBackendHealth();
                  void checkTraccarHealth();
                }}
                disabled={backendHealth === 'CHECKING' || traccarHealth === 'CHECKING'}
                className="!tw-h-10 !tw-w-10 !tw-rounded-lg !tw-bg-gtr-green !tw-text-white tw-transition-all tw-duration-200 hover:!tw-bg-gtr-green-hover disabled:!tw-bg-gtr-disabled disabled:!tw-text-gtr-disabled-text"
              >
                <RefreshOutlined className={backendHealth === 'CHECKING' || traccarHealth === 'CHECKING' ? 'tw-animate-spin' : ''} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box className="tw-grid tw-grid-cols-1 tw-gap-3 lg:tw-grid-cols-2">
          <ServiceHealthIndicator
            name="API"
            description="API principal do sistema"
            status={backendHealth}
            icon={<DnsOutlined />}
          />
          <ServiceHealthIndicator
            name="Telemetria / túnel de rastreamento"
            description="Serviço de conectividade com os dispositivos"
            status={traccarHealth}
            icon={<SensorsOutlined />}
          />
        </Box>
      </Paper>}

      <Box className="tw-grid tw-min-w-0 tw-gap-3" sx={{ gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
          {!isCustomerScope && <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Saúde dos Dispositivos"}>
              <Box className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={deviceHealth.map((it) => it.deviceCount)} labels={deviceHealth.map((it) => it.status)} type="donut" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>}

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Conectividade dos Dispositivos"}>
              <Box className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={deviceConnectivity.map((it) => it.deviceCount)} labels={deviceConnectivity.map((it) => it.status)} type="pie" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Status de Movimento"}>
              <Box className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart data={deviceMovementStatus.map((it) => it.deviceCount)} labels={deviceMovementStatus.map((it) => it.status)} colorsByLabel={movementStatusColors} type="polarArea" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>

          {!isCustomerScope && <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Alocação dos Dispositivos"}>
              <Box className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart compact data={deviceInventory.map((it) => it.deviceCount)} labels={deviceInventory.map((it) => it.status)} type="donut" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>}

          {!isCustomerScope && <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"de Dispositivos Ativos e Inativos"}>
              <Box className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart compact data={deviceActivationStatus.map((it) => it.deviceCount)} labels={deviceActivationStatus.map((it) => it.status)} type="pie" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>}

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={isCustomerScope ? "de Cercas Cadastradas" : "de Cercas por Cliente"}>
              <Box className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-justify-center">
                <TotalDonutChart compact data={geofencesByCustomer.map((it) => it.geofenceCount)} labels={geofencesByCustomer.map((it) => it.customerName)} type="donut" isLoading={isLoading} />
              </Box>
            </TotalItemCard>
          </Box>
      </Box>

      {DASHBOARD_DISTANCE_FUEL_ENABLED && <Box className="tw-grid tw-min-w-0 tw-gap-3" sx={{ gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"Km Rodado por Veículo (mês atual)"}>
              <Box className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-justify-center">
                <TotalRankingBarChart isLoading={isLoading} data={totalKmByVehicle} valueSuffix=" km" seriesName="Km rodado" />
              </Box>
            </TotalItemCard>
          </Box>

          <Box className="tw-min-w-0 tw-text-center">
            <TotalItemCard withAdornment={false} isLoading={isLoading} title={"Consumo de Combustível por Veículo (mês atual)"}>
              <Box className="tw-flex tw-h-full tw-w-full tw-min-w-0 tw-justify-center">
                <TotalRankingBarChart isLoading={isLoading} data={fuelConsumptionByVehicle} valueSuffix=" L" seriesName="Combustível consumido" />
              </Box>
            </TotalItemCard>
          </Box>
      </Box>}
    </Box>
  );
};

export default TrackingTab;
